/**
 * 订单态展示派生(跨 W-12/W-13 复用)。文案/分组/色按 state-machine.md §一,**不另立**。
 * 唯一合法转移真源在后端 OrderStateMachine;前端仅按 state×subState 渲染展示。
 */
import { ServerError } from '@/api/client';
import type { OrderListItem, OrderStateName, OrderSubStateName } from '@/api/order';

/** 业务码 → 取 ServerError.code,网络/未知层归 NETWORK_ERROR(与 rn-app 口径一致)。 */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';
export function errorCode(err: unknown): string {
  if (err instanceof ServerError) return err.code;
  return NETWORK_ERROR_CODE;
}
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ServerError) return err.message || fallback;
  return fallback;
}

/** 分 → "¥X.XX"。 */
export function formatAmount(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

/** ISO-8601 → "MM-DD HH:mm"(解析失败截取兜底)。 */
export function formatCreatedAtShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (m) return `${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
  return iso.slice(0, 16);
}

/** ISO-8601 → "YYYY-MM-DD HH:mm"(详情页全量)。 */
export function formatDateTime(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (m) return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
  return iso.slice(0, 16);
}

export type BadgeVariant = 'primary' | 'accent' | 'muted';

/**
 * 状态展示语气(设计系统极简单色:红仅用于需操作/告警态,其余走中性 ink/灰;完成态 ✓)。
 * - pay/attention:红(待支付 / 待响应取消 / 仲裁中)—— 圆点+红字,引导用户处理。
 * - active:前景 ink(进行中)—— 中性强调。
 * - done:灰 + ✓(已完成)。cancelled:灰(已取消)。
 */
export type StatusTone = 'pay' | 'active' | 'done' | 'cancelled' | 'attention';

/** 状态 Tag(子态优先)。文案=state-machine §一;variant 保留旧口径,tone 供订单列表「圆点+文字」渲染。 */
export function resolveStateBadge(
  state: OrderStateName,
  subState: OrderSubStateName,
): { label: string; variant: BadgeVariant; tone: StatusTone } {
  if (subState === 'PENDING_SELLER_RESPONSE')
    return { label: '待响应取消', variant: 'primary', tone: 'attention' };
  if (subState === 'IN_ARBITRATION')
    return { label: '仲裁中', variant: 'primary', tone: 'attention' };
  switch (state) {
    case 'PENDING_PAYMENT':
      return { label: '待支付', variant: 'muted', tone: 'pay' };
    case 'IN_PROGRESS':
      return { label: '进行中', variant: 'accent', tone: 'active' };
    case 'COMPLETED':
      return { label: '已完成', variant: 'muted', tone: 'done' };
    case 'CANCELLED':
      return { label: '已取消', variant: 'muted', tone: 'cancelled' };
    default:
      return { label: '未知', variant: 'muted', tone: 'cancelled' };
  }
}

export const BADGE_CLASS: Record<BadgeVariant, string> = {
  primary: 'badge badge-primary',
  accent: 'badge badge-accent',
  muted: 'badge badge-muted',
};

/** tone → 订单列表状态类(见 app.css `.order-status`)。 */
export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  pay: 'order-status order-status--pay',
  active: 'order-status order-status--active',
  done: 'order-status order-status--done',
  cancelled: 'order-status order-status--cancelled',
  attention: 'order-status order-status--attention',
};

/** 卖家分组顺序(state-machine §四:待响应取消→进行中→仲裁中→已完成→已取消)。 */
export const SELLER_GROUPS: ReadonlyArray<{
  key: string;
  label: string;
  match: (o: OrderListItem) => boolean;
}> = [
  {
    key: 'pending-cancel',
    label: '待响应取消',
    match: (o) => o.subState === 'PENDING_SELLER_RESPONSE',
  },
  {
    key: 'in-progress',
    label: '进行中',
    match: (o) =>
      (o.state === 'IN_PROGRESS' || o.state === 'PENDING_PAYMENT') &&
      o.subState !== 'PENDING_SELLER_RESPONSE' &&
      o.subState !== 'IN_ARBITRATION',
  },
  { key: 'in-arbitration', label: '仲裁中', match: (o) => o.subState === 'IN_ARBITRATION' },
  {
    key: 'completed',
    label: '已完成',
    match: (o) => o.state === 'COMPLETED' && o.subState === 'NONE',
  },
  {
    key: 'cancelled',
    label: '已取消',
    match: (o) => o.state === 'CANCELLED' && o.subState === 'NONE',
  },
];

export type OrderRow =
  | { type: 'section'; key: string; label: string }
  | { type: 'item'; key: string; order: OrderListItem };

/** 买家=扁平倒序(后端已 created_at DESC);卖家=按 SELLER_GROUPS 分组(组内保序)。 */
export function buildOrderRows(items: OrderListItem[], role: 'buyer' | 'seller'): OrderRow[] {
  if (role === 'buyer') {
    return items.map((o) => ({ type: 'item', key: `item-${o.orderId}`, order: o }));
  }
  const rows: OrderRow[] = [];
  for (const g of SELLER_GROUPS) {
    const inGroup = items.filter(g.match);
    if (inGroup.length === 0) continue;
    rows.push({ type: 'section', key: `section-${g.key}`, label: g.label });
    for (const o of inGroup) rows.push({ type: 'item', key: `item-${o.orderId}`, order: o });
  }
  return rows;
}
